import { Calendar as CalendarIcon, FlaskConical } from "lucide-react";
import { logger } from "@/utils/logger";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
	useState,
	useEffect,
	useLayoutEffect,
	useMemo,
	useCallback,
	useRef,
} from "react";
import { EventDetailModal } from "../components/modals/EventDetailModal";
import { ContentDetailModal } from "../components/modals/ContentDetailModal";
import { ContentScheduleModal } from "../components/modals/ContentScheduleModal";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "@/components/ui/dialog";
import { getCurrentUser, getUserProfile } from "@/services/data/users";
import { getBookingById } from "@/services/data/bookings";
import {
	getConnectedPlatforms,
	initializeUserPlatforms,
} from "@/services/data/socialConnections";
import {
	updateVideoPublications,
	getCalendarEvents,
} from "@/services/business/calendar";
import { getVideosWithPendingResources } from "@/services/data/userAssets";
import { hasServiceAccess } from "@/services/data/roles";
import type { ConnectedPlatform } from "@/services/data/socialConnections";
import type { CalendarEvent, Channel } from "@/services/business/calendar";
import { PageLoader } from "@/components/ui/page-loader";
import { useKnowledgeBaseStore } from "@/stores/knowledgeBaseStore";
import { useDisconnectedNetworks } from "@/hooks/useDisconnectedNetworks";
import { useDisconnectedNetworksStore } from "@/stores/disconnectedNetworksStore";
import {
	useCalendarStore,
	generateEventTasks,
	type Task,
} from "@/stores/calendarStore";
import { TaskDigest } from "@/components/TaskDigest";
import { KnowledgeBaseWizard } from "@/components/KnowledgeBaseWizard";
import { NetworkSidePanel } from "@/components/NetworkSidePanel";
import { ResourcesSidePanel } from "@/components/ResourcesSidePanel";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useIsMobile } from "@/hooks/useMobile";
import { useInfiniteCalendarScroll } from "@/hooks/useInfiniteCalendarScroll";
import { useStaffCalendarData } from "@/hooks/useStaffCalendarData";
import { useCsmCalendarData } from "@/hooks/useCsmCalendarData";
import { PreparationBookingScreen } from "./PreparationBookingScreen";
import { ShootingTopicsModal } from "@/components/modals/ShootingTopicsModal";
import { getYear } from "date-fns";
import { CalendarDateGroup } from "@/components/calendar/CalendarDateGroup";
import { DesktopOverlayPanel } from "@/components/calendar/DesktopOverlayPanel";
import { DesktopContentDetailOverlay } from "@/components/calendar/DesktopContentDetailOverlay";
import { LoadMoreButton } from "@/components/calendar/LoadMoreButton";
import type { CalendarItem } from "@/components/calendar/types";
import { useCalendarDigestCategories } from "@/hooks/useCalendarDigestCategories";

// Note: Content events are already expanded by date in calendarStore.ts
// Each content appears once per unique publication date

// Grouper les événements par date
const groupEventsByDate = (events: CalendarEvent[]) => {
	const grouped = new Map<string, CalendarEvent[]>();

	events.forEach((event) => {
		const dateKey = event.date.toLocaleDateString("fr-FR");
		if (!grouped.has(dateKey)) {
			grouped.set(dateKey, []);
		}
		grouped.get(dateKey)!.push(event);
	});

	return Array.from(grouped.entries())
		.sort((a, b) => {
			const dateA = new Date(a[1][0].date);
			const dateB = new Date(b[1][0].date);
			return dateA.getTime() - dateB.getTime();
		})
		.map(([key, evts]) => {
			const typePriority: Record<string, number> = {
				shooting: 0,
				editorial: 1,
				content: 2,
				ab_testing: 3,
			};
			const sorted = [...evts].sort((a, b) => {
				const pa = typePriority[a.type] ?? 2;
				const pb = typePriority[b.type] ?? 2;
				if (pa !== pb) return pa - pb;
				const timeA = a.currentDatePublications?.[0]?.time || a.time || "00:00";
				const timeB = b.currentDatePublications?.[0]?.time || b.time || "00:00";
				return timeA.localeCompare(timeB);
			});
			return [key, sorted] as [string, CalendarEvent[]];
		});
};

export function CalendarScreen({ userId: propUserId }: { userId?: string }) {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const isMobile = useIsMobile();
	const {
		setTaskDigestCategories,
		showNetworkPanel: showNetworkSidePanel,
		setShowNetworkPanel: setShowNetworkSidePanel,
		setCloseVideoPanelCallback,
		autoValidatedVideos,
	} = useCalendarStore();
	const initialExpandedCategories = (
		location.state as { expandedDigestCategories?: string[] }
	)?.expandedDigestCategories;
	const [events, setEvents] = useState<CalendarEvent[]>([]);
	const [connectedPlatforms, setConnectedPlatforms] = useState<
		ConnectedPlatform[]
	>([]);
	const [userId, setUserId] = useState<string | null>(propUserId || null);
	const [isLoading, setIsLoading] = useState(true);
	const { isComplete: isKnowledgeBaseComplete, refetch: refetchKnowledgeBase } =
		useKnowledgeBaseStore();
	const { disconnectedNetworks } = useDisconnectedNetworks(userId || undefined);
	const { fetchDisconnectedNetworks } = useDisconnectedNetworksStore();

	// Staff calendar data - check if user is VE, Monteur or Director
	const {
		isStaff,
		staffRole,
		staffEvents,
		directorEvents,
		isLoading: isStaffLoading,
	} = useStaffCalendarData(userId);

	// CSM calendar data - check if user is CSM and load tasks
	const {
		isCsm,
		csmEvents,
		isLoading: isCsmLoading,
	} = useCsmCalendarData(userId);

	const [showKnowledgeWizard, setShowKnowledgeWizard] = useState(false);
	const [showNetworkPanel, setShowNetworkPanel] = useState<string | null>(null); // event.id when open
	const [showResourcesPanel, setShowResourcesPanel] = useState(false);
	const [selectedResourceVideo, setSelectedResourceVideo] = useState<{
		id: string;
		title: string;
	} | null>(null);
	const [videosWithPendingResources, setVideosWithPendingResources] = useState<
		{ id: string; title: string | null; earliestPublicationDate: Date | null }[]
	>([]);
	const [hasUserAssetsAccess, setHasUserAssetsAccess] = useState(false);
	const [eventTasks, setEventTasks] = useState<
		Array<{ eventId: string; tasks: Task[] }>
	>([]);
	const [isTaskDigestCollapsed, setIsTaskDigestCollapsed] = useState(false);
	const [showEditorialBookingModal, setShowEditorialBookingModal] =
		useState(false);
	const [editorialBookingData, setEditorialBookingData] = useState<{
		userName: string;
		userEmail: string;
		productId: string;
		shootingDate: Date;
		shootingId: string | null;
	} | null>(null);
	const [showTopicsModal, setShowTopicsModal] = useState(false);
	const [topicsShootingData, setTopicsShootingData] = useState<{
		shootingId: string;
		shootingTitle?: string;
		shootingDate?: Date;
	} | null>(null);
	const lastScrollY = useRef(0);
	const todayRef = useRef<HTMLDivElement>(null);
	const hasScrolledToToday = useRef(false);
	const showKnowledgeWizardRef = useRef(false);
	const showNetworkSidePanelRef = useRef(false);
	const showResourcesPanelRef = useRef(false);
	const selectedEventRef = useRef<typeof selectedEvent>(null);
	const scrollToTodayTimeoutRef = useRef<number | null>(null);

	useEffect(() => {
		showKnowledgeWizardRef.current = showKnowledgeWizard;
	}, [showKnowledgeWizard]);

	useEffect(() => {
		showNetworkSidePanelRef.current = showNetworkSidePanel;
	}, [showNetworkSidePanel]);

	useEffect(() => {
		showResourcesPanelRef.current = showResourcesPanel;
	}, [showResourcesPanel]);

	// Mutual exclusion: close other panels when one opens
	useEffect(() => {
		if (showNetworkSidePanel && showKnowledgeWizard) {
			setShowKnowledgeWizard(false);
		}
		if (showNetworkSidePanel && showResourcesPanel) {
			setShowResourcesPanel(false);
		}
	}, [showNetworkSidePanel, showKnowledgeWizard, showResourcesPanel]);

	useEffect(() => {
		if (showResourcesPanel && showKnowledgeWizard) {
			setShowKnowledgeWizard(false);
		}
		if (showResourcesPanel && showNetworkSidePanel) {
			setShowNetworkSidePanel(false);
		}
	}, [
		showResourcesPanel,
		showKnowledgeWizard,
		showNetworkSidePanel,
		setShowNetworkSidePanel,
	]);

	// Close all overlays when navigating away from calendar (route change) on desktop
	useEffect(() => {
		// Reset overlays when location changes (user clicked sidebar menu)
		setSelectedEvent(null);
		setShowKnowledgeWizard(false);
		setShowNetworkSidePanel(false);
		setShowResourcesPanel(false);
		setSelectedResourceVideo(null);
	}, [location.pathname, setShowNetworkSidePanel]);

	// Memoize disconnectedNetworks to ensure stable reference
	const disconnectedNetworksMemo = useMemo(
		() => disconnectedNetworks,
		[JSON.stringify(disconnectedNetworks)],
	);

	// Use staff events if user is staff, otherwise use client events
	// CSM tasks are merged on top of the CSM's regular events
	const activeEvents: CalendarItem[] = useMemo(() => {
		let base: CalendarItem[];
		if (!isStaff) {
			base = events;
		} else if (
			staffRole === "realisateur" ||
			staffRole === "directeur_manager"
		) {
			base = directorEvents;
		} else {
			base = staffEvents;
		}
		// If user is CSM, merge CSM tasks into the calendar
		if (isCsm && csmEvents.length > 0) {
			return [...base, ...csmEvents];
		}
		return base;
	}, [
		isStaff,
		staffRole,
		staffEvents,
		directorEvents,
		events,
		isCsm,
		csmEvents,
	]);

	// Events are already expanded by date in calendarStore, just group them
	const groupedEvents = useMemo(() => {
		const result = groupEventsByDate(activeEvents as CalendarEvent[]);
		logger.debug(
			"[CalendarScreen] Video order by date:",
			result.map(([dateKey, evts]) => ({
				date: dateKey,
				events: evts.map((e) => ({
					type: e.type,
					title: e.title,
					time: e.currentDatePublications?.[0]?.time || e.time,
				})),
			})),
		);
		return result;
	}, [activeEvents]);

	// Transform to format expected by infinite scroll hook
	const dateGroups = useMemo(
		() => groupedEvents.map(([dateKey, evts]) => ({ dateKey, events: evts })),
		[groupedEvents],
	);

	// Infinite scroll for progressive loading
	const {
		visibleGroups,
		hasMoreUp,
		hasMoreDown,
		loadMoreUp,
		loadMoreDown,
		isLoadingUp,
		isLoadingDown,
		todayGroupIndex: visibleTodayIndex,
		setScrollContainer,
	} = useInfiniteCalendarScroll(dateGroups, { pageSize: 20 });

	const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
		null,
	);
	const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
	const [editingContent, setEditingContent] = useState<CalendarEvent | null>(
		null,
	);
	const [abTestingBlockedTitle, setAbTestingBlockedTitle] = useState<
		string | null
	>(null);
	const listContainerRef = useRef<HTMLDivElement>(null);

	// Sync selectedEvent ref for use in timeout callbacks
	useEffect(() => {
		selectedEventRef.current = selectedEvent;
	}, [selectedEvent]);

	// URL routing: ?eventId=<id> persists the open modal across refreshes and enables
	// deep-linking and browser back-button support.
	const eventIdParam = searchParams.get("eventId");

	// Sync modal state with URL ?eventId param (deep link, page refresh, browser back)
	useEffect(() => {
		if (!eventIdParam) {
			// URL param removed: close modal (browser back or programmatic close)
			setSelectedEvent(null);
			return;
		}
		if (events.length === 0) return;
		setSelectedEvent((prev) => {
			// Already showing the correct event — nothing to do
			if (prev && (prev.calendarId ?? prev.id) === eventIdParam) return prev;
			// Find and open the event that matches the URL param
			return (
				events.find((e) => (e.calendarId ?? e.id) === eventIdParam) ??
				prev ??
				null
			);
		});
	}, [events, eventIdParam]);

	// Register callback to close video panel from TaskSidebar
	useEffect(() => {
		setCloseVideoPanelCallback(() => {
			setSelectedEvent(null);
			setSearchParams({}, { replace: true });
		});
		return () => setCloseVideoPanelCallback(null);
	}, [setCloseVideoPanelCallback]);

	// Reset scroll anchor when panels/video close so we re-scroll to today
	useEffect(() => {
		if (
			!showKnowledgeWizard &&
			!showNetworkSidePanel &&
			!showResourcesPanel &&
			!selectedEvent
		) {
			hasScrolledToToday.current = false;
		}
	}, [
		showKnowledgeWizard,
		showNetworkSidePanel,
		showResourcesPanel,
		selectedEvent,
	]);

	// Scroll to today's date once data is loaded (and whenever panels close)
	useEffect(() => {
		// Always cancel any pending scroll
		if (scrollToTodayTimeoutRef.current) {
			clearTimeout(scrollToTodayTimeoutRef.current);
			scrollToTodayTimeoutRef.current = null;
		}

		// Don't scroll if wizard, panel, or video is open
		if (
			showKnowledgeWizard ||
			showNetworkSidePanel ||
			showResourcesPanel ||
			selectedEvent
		)
			return;

		if (!isLoading && groupedEvents.length > 0 && !hasScrolledToToday.current) {
			hasScrolledToToday.current = true;
			// Small delay to ensure DOM is rendered
			scrollToTodayTimeoutRef.current = window.setTimeout(() => {
				// Double-check panels/video are still closed when timeout fires
				if (
					showKnowledgeWizardRef.current ||
					showNetworkSidePanelRef.current ||
					showResourcesPanelRef.current ||
					selectedEventRef.current
				)
					return;

				if (todayRef.current) {
					const container = document.querySelector<HTMLElement>(
						'[data-app-main-scroll="true"]',
					);
					if (container) {
						const elementRect = todayRef.current.getBoundingClientRect();
						const containerRect = container.getBoundingClientRect();
						const offset = elementRect.top - containerRect.top;
						// Scroll so the element sits 24px from the top of the container
						container.scrollTo({
							top: container.scrollTop + offset - 24,
							behavior: "auto",
						});
					} else {
						todayRef.current.scrollIntoView({
							behavior: "auto",
							block: "start",
						});
					}
				}
			}, 100);
		}
	}, [
		isLoading,
		groupedEvents.length,
		showKnowledgeWizard,
		showNetworkSidePanel,
		showResourcesPanel,
		selectedEvent,
		isMobile,
	]);

	// Auto-collapse TaskDigest when scrolling down (mobile only)
	useEffect(() => {
		if (!isMobile) return;

		const handleScroll = () => {
			const currentScrollY = window.scrollY;
			const scrollDelta = currentScrollY - lastScrollY.current;

			// If scrolling down more than 30px, collapse the task digest
			if (scrollDelta > 30 && !isTaskDigestCollapsed) {
				setIsTaskDigestCollapsed(true);
			}

			lastScrollY.current = currentScrollY;
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, [isMobile, isTaskDigestCollapsed]);

	// Charger les données du calendrier et les connexions sociales
	useEffect(() => {
		const loadCalendarData = async () => {
			// Utiliser le propUserId s'il est fourni, sinon récupérer via getCurrentUser
			let currentUserId = propUserId;

			if (!currentUserId) {
				const userResult = await getCurrentUser();
				if (userResult.success && userResult.data) {
					currentUserId = userResult.data.id;
				}
			}

			if (!currentUserId) {
				setIsLoading(false);
				return;
			}

			setUserId(currentUserId);

			// Initialiser les plateformes si nécessaire
			await initializeUserPlatforms(currentUserId);

			// Charger les plateformes connectées
			const platformsResult = await getConnectedPlatforms(currentUserId);
			if (platformsResult.success && platformsResult.data) {
				setConnectedPlatforms(platformsResult.data);
			}

			// Charger les événements du calendrier
			const eventsResult = await getCalendarEvents(currentUserId);
			if (eventsResult.success && eventsResult.data) {
				setEvents(eventsResult.data);
			}

			// Vérifier l'accès au service user_assets avant de charger les ressources
			const accessResult = await hasServiceAccess(currentUserId, "user_assets");
			const hasAssetsAccess =
				accessResult.success && accessResult.data === true;
			setHasUserAssetsAccess(hasAssetsAccess);

			// Charger les vidéos avec ressources en attente seulement si accès autorisé
			if (hasAssetsAccess) {
				try {
					const pendingResources =
						await getVideosWithPendingResources(currentUserId);
					setVideosWithPendingResources(pendingResources);
				} catch (error) {
					logger.error("[CalendarScreen] Error loading pending resources:", error);
				}
			}

			setIsLoading(false);
		};

		loadCalendarData();
	}, [propUserId]);

	// Subscribe to social connections changes (from profile or other screens)
	const socialConnectionsVersion = useDisconnectedNetworksStore(
		(state) => state.socialConnectionsVersion,
	);

	useEffect(() => {
		if (!userId || socialConnectionsVersion === 0) return;
		// Refresh platforms when social connections change
		const refreshPlatforms = async () => {
			const platformsResult = await getConnectedPlatforms(userId);
			if (platformsResult.success && platformsResult.data) {
				setConnectedPlatforms(platformsResult.data);
			}
		};
		refreshPlatforms();
	}, [socialConnectionsVersion, userId]);

	// Memoize callbacks to prevent infinite loops
	const handleShowKnowledgeWizard = useCallback(() => {
		setShowKnowledgeWizard(true);
	}, []);

	const handleShowNetworkPanel = useCallback((eventId: string | null) => {
		setShowNetworkPanel(eventId);
	}, []);

	const handleSelectEvent = useCallback(
		(event: CalendarEvent) => {
			setSelectedEvent(event);
			setSearchParams(
				{ eventId: event.calendarId ?? event.id },
				{ replace: false },
			);
		},
		[setSearchParams],
	);

	// Generate event tasks when dependencies change
	useEffect(() => {
		const tasksArray: Array<{ eventId: string; tasks: Task[] }> = [];

		events.forEach((event) => {
			const tasks = generateEventTasks(event, {
				isKnowledgeBaseComplete,
				disconnectedNetworks: disconnectedNetworksMemo,
				connectedPlatforms,
				onShowKnowledgeWizard: handleShowKnowledgeWizard,
				onShowNetworkPanel: handleShowNetworkPanel,
				onSelectEvent: handleSelectEvent,
				currentNetworkPanelEventId: showNetworkPanel,
			});
			tasksArray.push({ eventId: event.id, tasks });
		});

		setEventTasks(tasksArray);
	}, [
		events,
		isKnowledgeBaseComplete,
		disconnectedNetworksMemo,
		connectedPlatforms,
		showNetworkPanel,
		handleShowKnowledgeWizard,
		handleShowNetworkPanel,
		handleSelectEvent,
	]);

	const handleEventClick = (event: CalendarEvent) => {
		// AB Testing cards - open detail panel directly (no validation gate)
		if (event.type === "ab_testing") {
			setSelectedEvent(event);
			setSearchParams(
				{ eventId: event.calendarId ?? event.id },
				{ replace: false },
			);
			return;
		}
		// Block click on in_production content - not yet validated by VE
		if (event.contentStatus === "in_production") {
			return;
		}
		// auto_validated: open detail view but feedbacks are blocked (handled inside modal)
		setSelectedEvent(event);
		setSearchParams(
			{ eventId: event.calendarId ?? event.id },
			{ replace: false },
		);
	};

	const handleCloseDialog = () => {
		setSelectedEvent(null);
		setSearchParams({}, { replace: true });
	};

	const handleSaveSchedule = async (
		publications: Array<{ channel: Channel; date: Date; time: string }>,
	): Promise<boolean> => {
		if (!editingContent) return false;

		const result = await updateVideoPublications(
			editingContent.id,
			publications,
			userId || undefined,
		);

		if (result.success) {
			setEvents((prevEvents) =>
				prevEvents.map((event) => {
					if (event.id === editingContent.id) {
						return {
							...event,
							publicationTimes: publications,
							channels: publications.map((p) => p.channel),
						};
					}
					return event;
				}),
			);

			setScheduleModalOpen(false);
			setEditingContent(null);
			return true;
		}
		return false;
	};

	const handleValidateAndLaunch = async (
		publications: Array<{ channel: Channel; date: Date; time: string }>,
	) => {
		if (!editingContent) return;
		const { updateVideoContent } = await import("@/services/data/videoContent");
		await updateVideoContent(editingContent.id, { status: "scheduled" });
		await handleSaveSchedule(publications);
	};

	const handleOpenEditorialBooking = async (shootingEvent: CalendarEvent) => {
		if (!userId || shootingEvent.type !== "shooting") return;

		try {
			const userResult = await getCurrentUser();
			if (!userResult.success || !userResult.data) {
				return;
			}

			const user = userResult.data;
			const profileResult = await getUserProfile(user.id);
			const profile = profileResult.success ? profileResult.data : null;

			const userName = profile
				? `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
					user.email?.split("@")[0] ||
					"Utilisateur"
				: user.email?.split("@")[0] || "Utilisateur";
			const userEmail = user.email || "";

			const bookingResult = await getBookingById(shootingEvent.id);
			if (!bookingResult.success || !bookingResult.data) {
				return;
			}

			const booking = bookingResult.data as any;
			const productId = booking.product_id;
			const shootingId = booking.shooting_id || null;

			if (!productId) {
				return;
			}

			setEditorialBookingData({
				userName,
				userEmail,
				productId,
				shootingDate: shootingEvent.date,
				shootingId,
			});
			setShowEditorialBookingModal(true);
		} catch (error) {
			logger.error("[CalendarScreen] Error opening editorial booking modal:", error);
		}
	};

	const handleEditorialBookingSuccess = async () => {
		setShowEditorialBookingModal(false);
		setEditorialBookingData(null);

		if (userId) {
			const eventsResult = await getCalendarEvents(userId);
			if (eventsResult.success && eventsResult.data) {
				setEvents(eventsResult.data);
			}
		}
	};

	const digestCategories = useCalendarDigestCategories({
		events,
		autoValidatedVideos,
		isKnowledgeBaseComplete,
		disconnectedNetworks,
		videosWithPendingResources,
		hasUserAssetsAccess,
		isMobile,
		navigate,
		setShowNetworkSidePanel,
		setShowKnowledgeWizard,
		setShowNetworkPanel,
		setSelectedEvent,
		setSelectedResourceVideo,
		setShowResourcesPanel,
		setEditingContent,
		setScheduleModalOpen,
	});

	// Sync digest categories to store for desktop sidebar
	// Use JSON comparison of structural data to avoid infinite loops from callback recreation
	const categoriesKey = useMemo(
		() =>
			JSON.stringify(
				digestCategories.map((c) => ({
					id: c.id,
					count: c.count,
					label: c.label,
					itemIds: c.items?.map((i) => i.id),
				})),
			),
		[digestCategories],
	);

	useEffect(() => {
		setTaskDigestCategories(digestCategories, initialExpandedCategories);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [categoriesKey, initialExpandedCategories]);

	// Scroll to top when wizard opens in desktop mode
	useLayoutEffect(() => {
		if (!isMobile && showKnowledgeWizard) {
			const container = document.querySelector<HTMLElement>(
				'[data-app-main-scroll="true"]',
			);
			if (container) {
				container.scrollTop = 0;
			} else {
				window.scrollTo(0, 0);
			}
		}
	}, [isMobile, showKnowledgeWizard]);

	// Scroll to top when network panel opens in desktop mode
	useLayoutEffect(() => {
		if (!isMobile && showNetworkSidePanel) {
			const container = document.querySelector<HTMLElement>(
				'[data-app-main-scroll="true"]',
			);
			if (container) {
				container.scrollTop = 0;
			} else {
				window.scrollTo(0, 0);
			}
		}
	}, [isMobile, showNetworkSidePanel]);

	const handleEventUpdate = useCallback(
		(eventId: string, patch: Partial<CalendarEvent>) => {
			setEvents((prev) => {
				const source = prev.find((e) => e.id === eventId);
				const linkedContentId = source?.linkedContentId;
				return prev.map((e) => {
					if (e.id === eventId) return { ...e, ...patch };
					if (patch.publicationTimes || patch.date) {
						if (linkedContentId && e.id === linkedContentId) {
							return {
								...e,
								...(patch.publicationTimes && {
									publicationTimes: patch.publicationTimes,
								}),
							};
						}
						if (e.linkedContentId === eventId) {
							return {
								...e,
								...(patch.publicationTimes && {
									publicationTimes: patch.publicationTimes,
								}),
							};
						}
					}
					return e;
				});
			});
			setSelectedEvent((prev) =>
				prev && prev.id === eventId ? { ...prev, ...patch } : prev,
			);
			// Keep the URL in sync when the event's calendarId changes (e.g. after
			// rescheduling the card moves to a new date slot and gets a new calendarId).
			if (patch.calendarId) {
				setSearchParams({ eventId: patch.calendarId }, { replace: true });
			}
			if ((patch.publicationTimes || patch.date) && userId) {
				getCalendarEvents(userId).then((eventsResult) => {
					if (eventsResult.success && eventsResult.data) {
						setEvents(eventsResult.data);
						setSelectedEvent((prev) => {
							if (!prev) return null;
							const refreshed = eventsResult.data.find((e) =>
								prev.calendarId && e.calendarId
									? e.calendarId === prev.calendarId
									: e.id === prev.id,
							);
							return refreshed ?? prev;
						});
					}
				});
			}
		},
		[userId, setSearchParams],
	);

	const handleValidate = useCallback(async () => {
		if (userId) {
			const eventsResult = await getCalendarEvents(userId);
			if (eventsResult.success && eventsResult.data) {
				setEvents(eventsResult.data);
				setSelectedEvent((prev) => {
					if (!prev) return null;
					const refreshed = eventsResult.data.find((e) =>
						prev.calendarId && e.calendarId
							? e.calendarId === prev.calendarId
							: e.id === prev.id,
					);
					return refreshed ?? prev;
				});
			}
		}
	}, [userId]);

	if (isLoading || isStaffLoading || isCsmLoading) {
		return <PageLoader variant="screen" />;
	}

	// Staff-only: hide client-specific panels and features
	const showClientOnlyFeatures = !isStaff;

	return (
		<div
			className={`relative bg-transparent text-white pb-32 v10-dashboard ${isMobile ? "min-h-screen" : "min-h-full"}`}
		>
			{/* Desktop: wizard overlay on top of content (does not cover sidebars) - client only */}
			{!isMobile && showClientOnlyFeatures && showKnowledgeWizard && userId && (
				<DesktopOverlayPanel>
					<KnowledgeBaseWizard
						userId={userId}
						variant="inline"
						onComplete={() => {
							setShowKnowledgeWizard(false);
							refetchKnowledgeBase();
						}}
						onBack={() => {
							setShowKnowledgeWizard(false);
							refetchKnowledgeBase();
						}}
					/>
				</DesktopOverlayPanel>
			)}

			{/* Desktop: network panel overlay on top of content (does not cover sidebars) - client only */}
			{!isMobile && showClientOnlyFeatures && showNetworkSidePanel && (
				<DesktopOverlayPanel>
					<NetworkSidePanel
						disconnectedNetworks={disconnectedNetworks}
						onClose={() => setShowNetworkSidePanel(false)}
						onNetworkConnected={() => {
							// Refetch disconnected networks after connection
							if (userId) fetchDisconnectedNetworks(userId);
						}}
						onAllNetworksConnected={() => {
							// Close panel when all networks connected - animation will trigger in TaskDigest
							setShowNetworkSidePanel(false);
							if (userId) fetchDisconnectedNetworks(userId);
						}}
					/>
				</DesktopOverlayPanel>
			)}

			{/* Desktop: resources panel overlay on top of content - client only */}
			{!isMobile &&
				showClientOnlyFeatures &&
				showResourcesPanel &&
				selectedResourceVideo && (
					<DesktopOverlayPanel>
						<ResourcesSidePanel
							videoId={selectedResourceVideo.id}
							videoTitle={selectedResourceVideo.title}
							onClose={() => {
								setShowResourcesPanel(false);
								setSelectedResourceVideo(null);
							}}
							onResourcesUpdated={async () => {
								// Refresh pending resources
								if (userId) {
									const pendingResources =
										await getVideosWithPendingResources(userId);
									setVideosWithPendingResources(pendingResources);
								}
							}}
						/>
					</DesktopOverlayPanel>
				)}

			{/* Desktop: content detail panel overlay on top of content - client only */}
			{!isMobile && showClientOnlyFeatures && (
				<DesktopContentDetailOverlay
					selectedEvent={selectedEvent}
					connectedPlatforms={connectedPlatforms}
					onClose={handleCloseDialog}
					onEventUpdate={handleEventUpdate}
					onValidate={handleValidate}
				/>
			)}

			<div className="relative z-10 max-w-4xl mx-auto pt-6">
				{/* Task Digest - sticky au scroll (mobile only, client only) */}
				{isMobile && showClientOnlyFeatures && (
					<div className="sticky top-6 z-20 pb-4">
						<div className="absolute inset-x-0 -top-6 bottom-0 -mx-4 bg-gradient-to-b from-black via-black/95 via-70% to-transparent pointer-events-none" />
						<div className="relative w-full">
							<TaskDigest
								categories={digestCategories}
								initialExpandedCategories={initialExpandedCategories}
								onTaskClicked={() => handleCloseDialog()}
								isCollapsed={isTaskDigestCollapsed}
								onToggleCollapse={() =>
									setIsTaskDigestCollapsed((prev) => !prev)
								}
							/>
						</div>
					</div>
				)}

				{/* Bouton charger plus vers le haut */}
				{hasMoreUp && (
					<div className="flex justify-center mb-4">
						<LoadMoreButton
							direction="up"
							isLoading={isLoadingUp}
							onClick={loadMoreUp}
						/>
					</div>
				)}

				{/* Liste des dates et événements */}
				<motion.div
					ref={listContainerRef}
					className="space-y-8"
					initial="hidden"
					animate="show"
					variants={{
						hidden: { opacity: 0 },
						show: {
							opacity: 1,
							transition: { staggerChildren: 0.06, delayChildren: 0.08 },
						},
					}}
				>
					{visibleGroups.map(({ dateKey, events: groupEvents }, index) => {
						const today = new Date();
						const now = new Date();
						today.setHours(0, 0, 0, 0);
						const eventDate = new Date(groupEvents[0].date);
						eventDate.setHours(0, 0, 0, 0);

						const allEventsOnDateArePast = groupEvents.every((evt) => {
							if (evt.type === "content" || evt.type === "ab_testing") {
								const evtDateTime = new Date(evt.date);
								if (
									evt.currentDatePublications &&
									evt.currentDatePublications.length > 0
								) {
									const firstPub = evt.currentDatePublications[0];
									if (firstPub.time) {
										const [hours, minutes] = firstPub.time
											.split(":")
											.map(Number);
										evtDateTime.setHours(hours, minutes, 0, 0);
									}
								}
								return evtDateTime < now;
							}
							return evt.date < now;
						});

						const isToday = eventDate.getTime() === today.getTime();
						const isFirstFutureDate =
							(isToday || !allEventsOnDateArePast) &&
							(index === 0 ||
								(() => {
									for (let i = 0; i < index; i++) {
										const prev = visibleGroups[i];
										const prevDate = new Date(prev.events[0].date);
										prevDate.setHours(0, 0, 0, 0);
										if (prevDate.getTime() === today.getTime()) return false;
										const prevHasFuture = !prev.events.every((evt) => {
											if (evt.type === "content" || evt.type === "ab_testing") {
												const evtDateTime = new Date(evt.date);
												if (
													evt.currentDatePublications &&
													evt.currentDatePublications.length > 0
												) {
													const firstPub = evt.currentDatePublications[0];
													if (firstPub.time) {
														const [hours, minutes] = firstPub.time
															.split(":")
															.map(Number);
														evtDateTime.setHours(hours, minutes, 0, 0);
													}
												}
												return evtDateTime < now;
											}
											return evt.date < now;
										});
										if (prevHasFuture) return false;
									}
									return true;
								})());

						const showYear =
							getYear(groupEvents[0].date) !== getYear(new Date());

						return (
							<CalendarDateGroup
								key={dateKey}
								dateKey={dateKey}
								events={groupEvents}
								isMobile={isMobile}
								isFirstFutureDate={isFirstFutureDate}
								todayRef={todayRef}
								showYear={showYear}
								eventTasks={eventTasks}
								staffRole={staffRole}
								onEventClick={handleEventClick}
								onOpenEditorialBooking={handleOpenEditorialBooking}
								onShowTopicsModal={(data) => {
									setTopicsShootingData(data);
									setShowTopicsModal(true);
								}}
							/>
						);
					})}
				</motion.div>

				{/* Bouton charger plus vers le bas */}
				{hasMoreDown && (
					<div className="flex justify-center mt-6 mb-4">
						<LoadMoreButton
							direction="down"
							isLoading={isLoadingDown}
							onClick={loadMoreDown}
						/>
					</div>
				)}

				{/* État vide si pas d'événements */}
				{dateGroups.length === 0 && (
					<div className="flex flex-col items-center justify-center py-24 text-center">
						<CalendarIcon className="h-16 w-16 text-white/10 mb-6" />
						<p className="text-lg text-white/40 tracking-wide">
							Aucun événement à venir
						</p>
					</div>
				)}
			</div>

			{/* Modals pour les détails des événements */}
			{(selectedEvent?.type === "shooting" ||
				selectedEvent?.type === "editorial") &&
				(() => {
					const now = new Date();
					let isPastEvent = false;

					if (selectedEvent) {
						const eventDateTime = new Date(selectedEvent.date);
						if (selectedEvent.time) {
							const [hours, minutes] = selectedEvent.time
								.split(":")
								.map(Number);
							eventDateTime.setHours(hours, minutes, 0, 0);
						}
						isPastEvent = eventDateTime < now;
					}

					return (
						<EventDetailModal
							event={selectedEvent as any}
							isOpen={!!selectedEvent}
							onClose={handleCloseDialog}
							isPastEvent={isPastEvent}
							onUpdate={async () => {
								// Recharger les événements du calendrier
								if (userId) {
									const eventsResult = await getCalendarEvents(userId);
									if (eventsResult.success && eventsResult.data) {
										setEvents(eventsResult.data);
									}
								}
							}}
						/>
					);
				})()}

			{/* Mobile: Content detail modal fullscreen - client only */}
			{isMobile &&
				showClientOnlyFeatures &&
				(selectedEvent?.type === "content" ||
					selectedEvent?.type === "ab_testing") && (
					<ContentDetailModal
						event={selectedEvent as any}
						isOpen={!!selectedEvent}
						onClose={handleCloseDialog}
						connectedPlatforms={connectedPlatforms}
						variant="modal"
						onEventUpdate={handleEventUpdate}
						onValidate={handleValidate}
					/>
				)}

			{/* Modal de gestion des planifications */}
			{editingContent && (
				<ContentScheduleModal
					isOpen={scheduleModalOpen}
					onClose={() => {
						setScheduleModalOpen(false);
						setEditingContent(null);
					}}
					contentTitle={editingContent.title}
					publications={editingContent.publicationTimes || []}
					connectedPlatforms={connectedPlatforms}
					onSave={handleSaveSchedule}
					onValidateAndLaunch={handleValidateAndLaunch}
					isMultihook={editingContent.isMultihook}
				/>
			)}

			{/* Knowledge Base Wizard - Mobile only (desktop handled above) - client only */}
			{isMobile && showClientOnlyFeatures && showKnowledgeWizard && userId && (
				<KnowledgeBaseWizard
					userId={userId}
					variant="card"
					onComplete={() => {
						setShowKnowledgeWizard(false);
						refetchKnowledgeBase();
					}}
					onBack={() => {
						setShowKnowledgeWizard(false);
						refetchKnowledgeBase();
					}}
				/>
			)}

			{/* AB Testing Blocked Modal */}
			<Dialog
				open={!!abTestingBlockedTitle}
				onOpenChange={() => setAbTestingBlockedTitle(null)}
			>
				<DialogContent className="bg-[hsl(var(--dash-card-bg))] border-white/10 max-w-sm">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2 text-white">
							<FlaskConical className="h-5 w-5 text-violet-400" />
							AB Testing non disponible
						</DialogTitle>
						<DialogDescription className="text-white/70 pt-2">
							L'AB Testing ne pourra être programmé que lorsque le contenu "
							{abTestingBlockedTitle}" sera validé.
						</DialogDescription>
					</DialogHeader>
				</DialogContent>
			</Dialog>

			{/* Editorial Booking Modal */}
			{editorialBookingData && (
				<PreparationBookingScreen
					open={showEditorialBookingModal}
					onClose={() => {
						setShowEditorialBookingModal(false);
						setEditorialBookingData(null);
					}}
					shootingDate={editorialBookingData.shootingDate}
					userName={editorialBookingData.userName}
					userEmail={editorialBookingData.userEmail}
					productId={editorialBookingData.productId}
					shootingId={editorialBookingData.shootingId}
					onBookingSuccess={handleEditorialBookingSuccess}
				/>
			)}

			{/* Shooting Topics Modal */}
			{topicsShootingData && (
				<ShootingTopicsModal
					isOpen={showTopicsModal}
					onClose={() => {
						setShowTopicsModal(false);
						setTopicsShootingData(null);
					}}
					shootingId={topicsShootingData.shootingId}
					shootingTitle={topicsShootingData.shootingTitle}
					shootingDate={topicsShootingData.shootingDate}
				/>
			)}
		</div>
	);
}
